import { memo, useCallback, useState } from "react";
import { BottomSheetModal, BottomSheetScrollView } from "@gorhom/bottom-sheet";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, Button, Banner } from "@src/components";
import { BottomSheetFormInput } from "@src/components/FormInput/BottomSheetFormInput";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { AppBottomSheet } from "@src/features/common/components/AppBottomSheet";

export type CreateTenantAdminData = {
  email: string;
};

interface CreateTenantAdminBottomSheetProps {
  bottomSheetRef: React.RefObject<BottomSheetModal | null>;
  onSave: (data: CreateTenantAdminData) => void;
}

export const CreateTenantAdminBottomSheet = memo(({ bottomSheetRef, onSave }: CreateTenantAdminBottomSheetProps) => {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();

  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<{ email?: string }>({});

  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
    if (value.trim()) setErrors((prev) => (prev.email ? { ...prev, email: undefined } : prev));
  }, []);

  const handleDismiss = useCallback(() => {
    setEmail("");
    setErrors({});
  }, []);

  const handleSave = useCallback(() => {
    const newErrors: { email?: string } = {};
    if (!email.trim()) newErrors.email = "Enter email address";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({ email });
    setErrors({});
    setTimeout(() => {
      bottomSheetRef.current?.dismiss();
    }, 100);
  }, [email, onSave, bottomSheetRef]);

  return (
    <AppBottomSheet
      bottomSheetRef={bottomSheetRef}
      enableDynamicSizing
      enablePanDownToClose
      keyboardBehavior="interactive"
      keyboardBlurBehavior="restore"
      android_keyboardInputMode="adjustResize"
      backgroundStyle={{ backgroundColor: theme.colors.surfaceCard }}
      onDismiss={handleDismiss}
    >
      <BottomSheetScrollView
        style={styles.container}
        contentContainerStyle={{ paddingBottom: bottom + theme.spacing["5xl"] }}
        keyboardDismissMode="on-drag"
        keyboardShouldPersistTaps="handled"
      >
        <Text textStyle="md/medium" style={styles.title}>
          Create new organisation admin
        </Text>

        <Banner
          variant="info"
          message="Granting organisation admin gives this user full administrative access to all sites, cameras, and user permissions across the organisation."
          containerStyle={styles.banner}
        />

        <View gap="3xl">
          <BottomSheetFormInput
            placeholder="Email address"
            value={email}
            onChangeText={handleEmailChange}
            size="medium"
            keyboardType="email-address"
            autoCapitalize="none"
            error={errors.email}
          />
        </View>

        <Button variant="primary" label="Invite" size="md" onPress={handleSave} style={styles.saveButton} />
      </BottomSheetScrollView>
    </AppBottomSheet>
  );
});

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingHorizontal: theme.spacing["5xl"],
  },
  title: {
    marginVertical: theme.spacing["4xl"],
  },
  banner: {
    marginBottom: theme.spacing["4xl"],
  },
  saveButton: {
    marginTop: theme.spacing["4xl"],
  },
}));
